/**
 * Class name helper.
 *
 * BLUEPRINT.md suggests `clsx` + `tailwind-merge`, but those deps may not be
 * installed yet. This implementation is a small, dependency-free alternative
 * that:
 * - accepts strings / numbers / arrays / objects (truthy -> include key)
 * - dedupes tokens
 * - keeps last occurrence when duplicates exist (last-write-wins)
 *
 * When you add `clsx` and `tailwind-merge`, we can swap this implementation
 * to those without changing call sites.
 */

export type ClassValue =
  | string
  | number
  | null
  | undefined
  | false
  | Record<string, unknown>
  | ClassValue[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pushTokens(out: string[], value: ClassValue): void {
  if (!value) return;

  if (typeof value === "string" || typeof value === "number") {
    const s = String(value).trim();
    if (!s) return;
    out.push(...s.split(/\s+/g));
    return;
  }

  if (Array.isArray(value)) {
    for (const v of value) pushTokens(out, v);
    return;
  }

  if (isRecord(value)) {
    for (const [k, v] of Object.entries(value)) {
      if (v) pushTokens(out, k);
    }
  }
}

function dedupeKeepLast(tokens: string[]): string[] {
  const lastIndex = new Map<string, number>();
  tokens.forEach((t, idx) => lastIndex.set(t, idx));

  const result: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (lastIndex.get(t) === i) result.push(t);
  }
  return result;
}

export function cn(...values: ClassValue[]): string {
  const tokens: string[] = [];
  for (const v of values) pushTokens(tokens, v);
  return dedupeKeepLast(tokens).join(" ");
}
